// @flow

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Container,
    Typography,
    Box,
    Paper,
    Alert,
} from "@mui/material";
import { useAppContext } from "../context/AppContext";
import { useLanguage } from "../i18n";
import LanguageSelector from "../components/LanguageSelector";

import ProductsToReturnStep from "./steps/ProductsToReturnStep";
import PickupLocationStep from "./steps/PickupLocationStep";
import PickupDateStep from "./steps/PickupDateStep";
import ConfirmEmailStep from "./steps/ConfirmEmailStep";

const ManageSaleOrderReturnRequestPage = (): any => {
    const { state } = useAppContext();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        if (!state.returnRequest) {
            navigate("/");
        }
    }, [state.returnRequest, navigate]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    useEffect(() => {
        if (state.returnRequest?.state === "COMPLETED") {
            setActiveStep(4);
        }
    }, [state.returnRequest]);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <LanguageSelector />
                </Box>

                {/* Descriptive, friendly paragraph */}
                <Typography variant="h6" mb={2} fontWeight={600} color="primary">
                    {t('requestReturnTitle')}
                </Typography>

                <Typography variant="body1" mb={3} color="textSecondary">
                    {t('requestReturnDescription')}{" "}
                    <strong>{state.returnRequest?.saleOrderNo}</strong> {t('from')}{" "}
                    <strong>{state.returnRequest?.saleOrderDate ? new Date(state.returnRequest.saleOrderDate).toISOString().split('T')[0] : ''}</strong>, {t('orderedBy')}{" "}
                    <strong>{state.returnRequest?.customer?.name}</strong>. {t('guideMessage')}
                </Typography>

                {state.returnRequest?.state === 'COMPLETED' && (
                    <Alert 
                        severity="success" 
                        sx={{ 
                            mt: 3,
                            mb: 3,
                            '& .MuiAlert-message': {
                                fontSize: '1rem',
                            }
                        }}
                    >
                        {t('returnRequestCompleted')}{' '}
                        <strong>{state.returnRequest?.customer?.email}</strong>
                    </Alert>
                )}

                <Box sx={{ maxWidth: 700 }}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        <Step expanded={activeStep >= 0}>
                            <StepLabel>{t('stepSelectProducts')}</StepLabel>
                            <StepContent>
                                <ProductsToReturnStep 
                                    stepNo={0}
                                    activeStep={activeStep}
                                    handleNext={handleNext}
                                />
                            </StepContent>
                        </Step>

                        <Step expanded={activeStep >= 1}>
                            <StepLabel>
                                {t('stepPickupLocation')}
                            </StepLabel>
                            <StepContent>
                                <PickupLocationStep 
                                    stepNo={1}
                                    activeStep={activeStep}
                                    handleNext={handleNext}
                                />
                            </StepContent>
                        </Step>

                        <Step expanded={activeStep >= 2}>
                            <StepLabel>
                                {t('stepPickupDate')}
                            </StepLabel>
                            <StepContent>
                                <PickupDateStep 
                                    stepNo={2}
                                    activeStep={activeStep}
                                    handleNext={handleNext}
                                />
                            </StepContent>
                        </Step>

                        <Step expanded={activeStep >= 3}>
                            <StepLabel>
                                {t('stepConfirmEmail')}
                            </StepLabel>
                            <StepContent>
                                <Typography variant="caption">{t('confirmEmailDescription')}</Typography>
                                
                                <Stack>
                                    <ConfirmEmailStep />
                                    
                                    {(activeStep === 2) ? 
                                        (<Button
                                            variant="contained"
                                            onClick={handleNext}
                                            sx={{ mt: 1, mr: 1 }}
                                        >
                                            {t('continue')}
                                        </Button>) : null
                                    }
                                </Stack>
                            </StepContent>
                        </Step>

                    </Stepper>

                    
                    {/* {activeStep === steps.length && (
                        <Paper square elevation={0} sx={{ p: 3 }}>
                            <Typography>
                                {t('allStepsCompleted')}
                            </Typography>
                            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                                {t('reset')}
                            </Button>
                        </Paper>
                    )} */}
                </Box>
            </Paper>
        </Container>
    );
};

export default ManageSaleOrderReturnRequestPage;
